import Badge from "@/v2-ui/badge"
import Spinner from "@/v2-ui/spinner"
import cn from "@/v2-ui/utils/utils.cn"
import FeatureNotice from "@/v2-backoffice-client/feature/FeatureNotice"
import Link from "@/v2-backoffice-client/components/link"
import { customerSheetHref } from "@/v2-backoffice-client/license/changes/license.changes.constants"
import type { FeatureLockImpactQuery } from "@/v2-backoffice-client/feature/access/__types__/feature.access.lockImpact.query"

export type FeatureLockImpactValue = NonNullable<
  FeatureLockImpactQuery["getFeatureLockImpact"]
>

export type FeatureLockImpactProps = {
  impact: FeatureLockImpactValue | undefined
  loading: boolean
  error: Error | undefined
  /** True while the saved row already sits behind the package lock. */
  isLockedToday: boolean
  className?: string
}

function plural(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`
}

/**
 * The blast radius of making one feature package-only, shown in the
 * Feature access panel the moment an operator picks Package-only (D14:
 * enforcement ships dark and is switched on one feature at a time; this is
 * the number to read before saving). Who keeps it (a live license whose
 * plan or add-on grants a package containing the code), who loses it, how
 * many of those were active in the last 30 days, and the customers behind
 * the loss, each a link to its panel, with the fix each one needs.
 *
 * Presentational: the form that owns the Save owns the query, so the same
 * numbers can sit on the button the operator presses.
 */
function FeatureLockImpact(props: FeatureLockImpactProps) {
  const { impact, loading, error, isLockedToday, className } = props

  if (loading && !impact) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 text-xs text-muted-foreground",
          className,
        )}
      >
        <Spinner displaySize="xs" />
        Working out who would lose this feature...
      </div>
    )
  }
  if (error || !impact) {
    return (
      <FeatureNotice tone="destructive" className={className}>
        Could not work out the impact of locking this feature.
      </FeatureNotice>
    )
  }

  const isGrantedByNothing = impact.PlanCount === 0 && impact.AddOnCount === 0
  const verb = isLockedToday ? "lose" : "would lose"
  const tone =
    isGrantedByNothing || impact.LosingUserCount30d > 0 ? "warning" : "info"

  return (
    <div className={cn("space-y-2", className)}>
      <FeatureNotice tone={tone}>
        <span className="font-medium">
          {impact.LosingUserCount} of {impact.ActiveUserCount} active users{" "}
          {verb} it
        </span>
        {impact.LosingUserCount30d > 0 &&
          ` (${impact.LosingUserCount30d} signed in during the last 30 days)`}
        , across {plural(impact.LosingCustomerCount, "customer")}. Granted by{" "}
        {plural(impact.FeatureGroupCount, "package")},{" "}
        {plural(impact.PlanCount, "license")} and{" "}
        {plural(impact.AddOnCount, "add-on")}.
        {isGrantedByNothing &&
          " No license or add-on carries a package with this feature yet, so the lock would reach every customer: pick the packages that include it, and check that a license or add-on carries them."}
        {impact.LosingUserWithoutCustomerCount > 0 &&
          ` ${plural(impact.LosingUserWithoutCustomerCount, "losing user")} belong to no customer, so no license can reach them: the fix is linking them, not the catalog.`}
      </FeatureNotice>

      {impact.Customers.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border bg-card text-xs">
          {impact.Customers.map((customer) => {
            const name =
              customer.Name ??
              customer.ExternalCustomerId ??
              `#${customer.CustomerId}`
            return (
              <li
                key={customer.CustomerId}
                className="flex items-center justify-between gap-3 px-2.5 py-1.5"
              >
                {customer.ExternalCustomerId ? (
                  <Link
                    to={customerSheetHref(customer.ExternalCustomerId)}
                    variant="muted"
                    size="xs"
                    iconSrc={null}
                    className="min-w-0 truncate text-foreground"
                    title={`Open ${name}`}
                  >
                    {name}
                  </Link>
                ) : (
                  <span className="min-w-0 truncate">{name}</span>
                )}
                <span className="flex shrink-0 items-center gap-2 tabular-nums text-muted-foreground">
                  {plural(customer.LosingUserCount, "user")}
                  {customer.LosingUserCount30d > 0 &&
                    ` · ${customer.LosingUserCount30d} active`}
                  <Badge
                    size="xs"
                    variant={customer.HasLiveLicense ? "warning" : "secondary"}
                  >
                    {customer.HasLiveLicense
                      ? "license lacks the package"
                      : "no live license"}
                  </Badge>
                </span>
              </li>
            )
          })}
        </ul>
      )}
      {impact.LosingCustomerCount > impact.Customers.length && (
        <p className="text-xs text-muted-foreground">
          The {impact.Customers.length} customers losing the most are listed;
          the Unlicensed Usage board has the rest.
        </p>
      )}
    </div>
  )
}

export default FeatureLockImpact
